import type { HelpContent } from './types';

export const auditLogHelp: HelpContent = {
  pageTitle: 'Audit Log',
  description: 'A chronological record of all significant actions taken within Access Projects — who did what, and when.',

  functionality: {
    overview: 'The Audit Log provides a full, tamper-evident record of actions taken across Access Projects. It logs changes to settings, projects, resources, budgets, and clients, giving Administrators and authorised users full visibility of system activity for governance and compliance purposes.',

    keyFeatures: [
      {
        label: 'Full activity history',
        detail: 'Every significant action in Access Projects is logged — including who performed it, what was changed, and when.',
      },
      {
        label: 'Category filtering',
        detail: 'Filter the audit log by category — Settings, Project, Resource, Budget, or Client — to focus on the area of activity you need to review.',
      },
      {
        label: 'Search',
        detail: 'Search the audit log by user name, action description, or affected record to find specific events quickly.',
      },
      {
        label: 'Chronological order',
        detail: 'Entries are displayed in reverse chronological order, with the most recent activity shown first.',
      },
      {
        label: 'Export to Excel',
        detail: 'Export the full audit log or a filtered subset to Excel for compliance reporting or investigation purposes.',
      },
    ],

    sectionsAndFields: [
      {
        name: 'Search bar',
        description: 'Filter audit log entries in real time by searching for a user name, action, or affected record.',
      },
      {
        name: 'Category filter',
        description: 'Filter entries by category — All, Settings, Project, Resource, Budget, or Client.',
      },
      {
        name: 'Audit log table',
        description: 'Lists all audit entries with columns for timestamp, user, category, action description, and affected record.',
      },
      {
        name: 'Timestamp',
        description: 'The date and time the action was performed, displayed in your local timezone.',
      },
      {
        name: 'User',
        description: 'The name of the user who performed the action.',
      },
      {
        name: 'Category badge',
        description: 'A colour-coded badge indicating the category of the audit entry — Settings, Project, Resource, Budget, or Client.',
      },
      {
        name: 'Action',
        description: 'A description of what was done — for example, "Updated project status" or "Changed billing rate".',
      },
      {
        name: 'Export button',
        description: 'Downloads the current audit log view as an Excel file, reflecting any active search or category filters.',
      },
    ],

    useCases: [
      'Reviewing who made a specific change to a project or setting.',
      'Investigating unexpected changes to budgets or financial data.',
      'Producing an activity report for compliance or governance purposes.',
      'Checking when a specific configuration change was made.',
    ],
  },

  userGuide: {
    gettingStarted: 'The Audit Log loads with all recent activity across Access Projects in reverse chronological order. Use the category filter and search bar to find specific events.',
    steps: [
      {
        title: 'How do I find a specific event?',
        items: [
          { text: 'Use the search bar to search by user name, action, or affected record.' },
          { text: 'Use the category filter to narrow results to a specific area — for example, filter to "Budget" to see all budget-related changes.' },
          { text: 'Combine search and category filter for more precise results.' },
        ],
      },
      {
        title: 'How do I export the audit log?',
        items: [
          { text: 'Apply any filters or search terms to refine the data.' },
          { text: 'Click the Export button in the page header.' },
          { text: 'The audit log downloads as an Excel file reflecting the current filtered view.' },
        ],
      },
    ],
    proTips: [
      'For compliance or investigation purposes, export the unfiltered audit log first to ensure you have a complete record before narrowing your view.',
      'Use the "Settings" category filter to quickly review any configuration changes made by Administrators.',
    ],
  },

  faq: [
    {
      question: 'Who can access the Audit Log?',
      answer: 'Access to the Audit Log is controlled by your access permissions. If you cannot see it, contact your Administrator to review your access.',
    },
    {
      question: 'How far back does the audit log go?',
      answer: 'The audit log retains all recorded activity within the system. The retention period may be subject to your organisation\'s data policies — contact your Administrator for details.',
    },
    {
      question: 'Can entries in the audit log be deleted or edited?',
      answer: 'No. The audit log is a read-only, tamper-evident record. Entries cannot be edited or deleted to ensure the integrity of the activity history.',
    },
    {
      question: 'Why is an action I performed not showing in the audit log?',
      answer: 'Not all actions are logged — only significant changes such as updates to settings, projects, resources, budgets, and clients. Minor navigation or view actions are not recorded.',
    },
    {
      question: 'Can I see what the data looked like before a change was made?',
      answer: 'The audit log records the action taken and the affected record. Detailed before-and-after values may not be available for all action types.',
    },
  ],

  relatedPages: [
    { label: 'Settings', path: '/settings' },
  ],
};
